/**
 * MarketDataProvider — العقد الوحيد بين الواجهة وأي مصدر بيانات.
 *
 * قاعدة معمارية ملزمة:
 *   UI → Hooks/Services → Domain Interfaces → Data Provider → Backend
 *
 * الواجهة لا تعرف شيئاً عن: أسماء المصادر التجارية، مفاتيح API، جداول قاعدة
 * البيانات، ولا عن أي خوارزمية. كل ما تراه هو هذا العقد.
 *
 * PHASE 1: التنفيذ الوحيد هو DemoMarketDataProvider.
 * لاحقاً: ProductionMarketDataProvider يطبّق نفس الواجهة بدون تغيير أي Component.
 */

import type {
  CityName,
  DistrictDetail,
  DistrictSuggestion,
  MapMetrics,
  MapMode,
  MarketPulse,
  Opportunity,
  OpportunityFilter,
  PropertyCategoryAvailability,
  ReportListItem,
  ReportPreview,
  ReportType,
  ValuationRequest,
  ValuationResult,
  WatchAlertConditions,
  WatchItem,
} from './types';

/** تصنيف الأخطاء الذي تفهمه الواجهة. لا يحمل تفاصيل داخلية. */
export type ProviderErrorCode =
  /** خطأ اتصال/خادم — تُعرض شاشة Error مع إعادة المحاولة. */
  | 'unavailable'
  /** لا توجد بيانات كافية لإعطاء نطاق موثوق — تُعرض شاشة No Data، وليست خطأ. */
  | 'insufficient_data'
  /** مدخلات غير صالحة. */
  | 'invalid_input'
  /** الحي غير معروف. */
  | 'not_found'
  /** الميزة نفسها غير مطلقة بعد — تحتاج محرّكاً لم يُنفّذ (قيد الإطلاق). */
  | 'feature_unavailable';

/** تشخيص الرفض من المحرك — أرقام فعلية لا نص عام. */
export interface RejectionDetail {
  headline: string;
  facts: string[];
  suggestion: string | null;
}

export class ProviderError extends Error {
  readonly code: ProviderErrorCode;
  /** يُرفق عند `insufficient_data` ليعرض السبب الحقيقي بدل جملة ثابتة. */
  readonly rejection?: RejectionDetail | null;

  constructor(code: ProviderErrorCode, message: string, rejection?: RejectionDetail | null) {
    super(message);
    this.name = 'ProviderError';
    this.code = code;
    this.rejection = rejection ?? null;
  }
}

export interface GetDistrictParams {
  districtId?: string;
  name?: string;
  city?: CityName;
}

export interface GetMapMetricsParams {
  city: CityName;
  mode: MapMode;
}

export interface GetOpportunitiesParams extends OpportunityFilter {
  limit?: number;
}

export interface WatchlistMutation {
  districtId: string;
  conditions?: Partial<WatchAlertConditions>;
}

export interface MarketDataProvider {
  /** أصل البيانات — الواجهة تستخدمه لعرض مؤشر LIVE / DEMO. */
  readonly origin: 'demo' | 'production';
  /** اسم وصفي للتشخيص فقط — لا يُعرض اسم أي مصدر تجاري. */
  readonly label: string;

  getCities(): Promise<CityName[]>;
  /** أي فئات العقار في البيانات صفقات تكفي لعرضها خياراً للتقييم. */
  getPropertyCategories(): Promise<PropertyCategoryAvailability>;
  searchDistricts(query: string, limit?: number): Promise<DistrictSuggestion[]>;

  getDistrict(params: GetDistrictParams): Promise<DistrictDetail>;
  getMapMetrics(params: GetMapMetricsParams): Promise<MapMetrics>;
  getValuation(request: ValuationRequest): Promise<ValuationResult>;
  getOpportunities(params: GetOpportunitiesParams): Promise<Opportunity[]>;

  getWatchlist(): Promise<WatchItem[]>;
  addToWatchlist(m: WatchlistMutation): Promise<WatchItem[]>;
  removeFromWatchlist(districtId: string): Promise<WatchItem[]>;
  updateWatchConditions(districtId: string, conditions: WatchAlertConditions): Promise<WatchItem[]>;

  getMarketPulse(city?: CityName): Promise<MarketPulse>;

  listReports(): Promise<ReportListItem[]>;
  getReport(type: ReportType, subjectId?: string): Promise<ReportPreview>;
}
